import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { getActuatorUnit } from '../../utils/actionUtils';
import { useCarePlans } from '../../utils/reactQueryHooks';
import '../../styles/automation.css';

// Custom Calendar Picker Component
const CalendarPicker = ({ value, onChange, placeholder, required, error, minDate }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);
    const calendarRef = useRef(null);

    useEffect(() => {
        if (value) {
            setSelectedDate(new Date(value));
        }
    }, [value]);

    // Close calendar when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const getDaysInMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const formatDate = (date) => {
        if (!date) return '';
        return date.toISOString().split('T')[0];
    };

    const handleDateSelect = (day) => {
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        setSelectedDate(newDate);
        onChange(formatDate(newDate));
        setIsOpen(false);
    };

    const goToPreviousMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const goToNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const isDateDisabled = (day) => {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of today
        
        // Compare only the date part, not the time
        const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        
        // Disable past dates (before today)
        if (dateOnly < today) {
            return true;
        }
        
        // Also respect minDate if provided
        if (minDate) {
            const minDateOnly = new Date(new Date(minDate).getFullYear(), new Date(minDate).getMonth(), new Date(minDate).getDate());
            return dateOnly < minDateOnly;
        }
        
        return false;
    };

    const isToday = (day) => {
        const today = new Date();
        return day === today.getDate() && 
               currentDate.getMonth() === today.getMonth() && 
               currentDate.getFullYear() === today.getFullYear();
    };

    const isSelected = (day) => {
        if (!selectedDate) return false;
        return day === selectedDate.getDate() && 
               currentDate.getMonth() === selectedDate.getMonth() && 
               currentDate.getFullYear() === selectedDate.getFullYear();
    };

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const daysInMonth = getDaysInMonth(currentDate);
    const firstDayOfMonth = getFirstDayOfMonth(currentDate);

    return (
        <div className="calendar-picker-container" ref={calendarRef}>
            <div className="calendar-input-wrapper">
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    required={required}
                    className={`calendar-input ${error ? 'error' : ''}`}
                    onClick={() => setIsOpen(!isOpen)}
                    readOnly
                />
                <button 
                    type="button" 
                    className="calendar-picker-icon"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <i className="ri-calendar-line"></i>
                </button>
            </div>
            
            {isOpen && (
                <div className="calendar-dropdown">
                    <div className="calendar-header">
                        <button 
                            type="button" 
                            className="calendar-nav-btn"
                            onClick={goToPreviousMonth}
                        >
                            <i className="ri-arrow-left-s-line"></i>
                        </button>
                        <span className="calendar-month-year">
                            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                        </span>
                        <button 
                            type="button" 
                            className="calendar-nav-btn"
                            onClick={goToNextMonth}
                        >
                            <i className="ri-arrow-right-s-line"></i>
                        </button>
                    </div>
                    
                    <div className="calendar-days-header">
                        {dayNames.map(day => (
                            <div key={day} className="calendar-day-header">{day}</div>
                        ))}
                    </div>
                    
                    <div className="calendar-days">
                        {Array.from({ length: firstDayOfMonth }, (_, i) => (
                            <div key={`empty-${i}`} className="calendar-day empty"></div>
                        ))}
                        {Array.from({ length: daysInMonth }, (_, i) => {
                            const day = i + 1;
                            const disabled = isDateDisabled(day);
                            return (
                                <button
                                    key={day}
                                    type="button"
                                    className={`calendar-day ${isToday(day) ? 'today' : ''} ${isSelected(day) ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                                    onClick={() => !disabled && handleDateSelect(day)}
                                    disabled={disabled}
                                >
                                    {day}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

CalendarPicker.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    error: PropTypes.bool,
    minDate: PropTypes.string
};

const AutomationPlanModal = ({ 
    isVisible, 
    onClose, 
    onSave, 
    plan = null, 
    actionType
}) => {
    
    // Fetch care plans for selection
    const { data: carePlans = [] } = useCarePlans({});
    const [formData, setFormData] = useState({
        name: '', // Action plan name
        carePlanName: '', // Care plan name (for new care plans)
        description: '', // Care plan description (for new care plans)
        type: 'seasonal',
        recurrence: 'monthly',
        startDate: '',
        endDate: '',
        repeatsAnnually: false,
        events: [],
        carePlanSelection: 'new', // 'new' or 'existing'
        selectedCarePlanId: null
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    // Initialize form data when editing
    useEffect(() => {
        if (plan) {
            setFormData({
                name: plan.name || '',
                type: plan.type || 'seasonal',
                recurrence: plan.recurrence || 'monthly',
                startDate: plan.startDate || '',
                endDate: plan.endDate || '',
                repeatsAnnually: plan.repeatsAnnually || false,
                events: plan.events || [],
                // For editing, we're using an existing care plan
                carePlanSelection: 'existing',
                selectedCarePlanId: plan.carePlanId || null,
                carePlanName: '', // Not needed for editing
                description: '' // Not needed for editing
            });
        } else {
            // Reset form for new plan
            setFormData({
                name: '', // Action plan name
                carePlanName: '', // Care plan name
                description: '', // Care plan description
                type: 'seasonal',
                recurrence: 'monthly',
                startDate: '',
                endDate: '',
                repeatsAnnually: false,
                events: [],
                carePlanSelection: 'new',
                selectedCarePlanId: null
            });
        }
        setErrors({});
    }, [isVisible, plan]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            const newData = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };
            
            // If start or end date changed, only clear events that are actually outside the new range
            if (name === 'startDate' || name === 'endDate') {
                const newStartDate = name === 'startDate' ? value : prev.startDate;
                const newEndDate = name === 'endDate' ? value : prev.endDate;
                
                if (newStartDate && newEndDate) {
                    const validMonths = getValidMonthsForDateRange(newStartDate, newEndDate);
                    const validMonthValues = validMonths.map(m => m.value);
                    
                    // Only clear events that are actually outside the new valid range
                    newData.events = prev.events.map(event => {
                        if (event.month && !validMonthValues.includes(event.month)) {
                            console.log(`Clearing event with month ${event.month} - not in valid range:`, validMonthValues);
                            return { ...event, month: '', date: '' };
                        }
                        return event;
                    });
                }
            }
            
            return newData;
        });
        
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Helper function to get valid months for a specific date range (used in handleInputChange)
    const getValidMonthsForDateRange = (startDateStr, endDateStr) => {
        if (!startDateStr) {
            return [
                { value: 'Jan', name: 'January', num: 0 },
                { value: 'Feb', name: 'February', num: 1 },
                { value: 'Mar', name: 'March', num: 2 },
                { value: 'Apr', name: 'April', num: 3 },
                { value: 'May', name: 'May', num: 4 },
                { value: 'Jun', name: 'June', num: 5 },
                { value: 'Jul', name: 'July', num: 6 },
                { value: 'Aug', name: 'August', num: 7 },
                { value: 'Sep', name: 'September', num: 8 },
                { value: 'Oct', name: 'October', num: 9 },
                { value: 'Nov', name: 'November', num: 10 },
                { value: 'Dec', name: 'December', num: 11 }
            ];
        }
        
        const startDate = new Date(startDateStr);
        const endDate = endDateStr ? new Date(endDateStr) : new Date(startDate.getFullYear() + 1, 11, 31);
        
        const months = [
            { value: 'Jan', name: 'January', num: 0 },
            { value: 'Feb', name: 'February', num: 1 },
            { value: 'Mar', name: 'March', num: 2 },
            { value: 'Apr', name: 'April', num: 3 },
            { value: 'May', name: 'May', num: 4 },
            { value: 'Jun', name: 'June', num: 5 },
            { value: 'Jul', name: 'July', num: 6 },
            { value: 'Aug', name: 'August', num: 7 },
            { value: 'Sep', name: 'September', num: 8 },
            { value: 'Oct', name: 'October', num: 9 },
            { value: 'Nov', name: 'November', num: 10 },
            { value: 'Dec', name: 'December', num: 11 }
        ];
        
        const startMonth = startDate.getMonth();
        const startYear = startDate.getFullYear();
        const endMonth = endDate.getMonth();
        const endYear = endDate.getFullYear();
        
        return months.filter(month => {
            if (startYear === endYear) {
                return month.num >= startMonth && month.num <= endMonth;
            }
            return (month.num >= startMonth) || (month.num <= endMonth);
        });
    };

    const addEvent = () => {
        const newEvent = formData.type === 'seasonal' 
            ? { month: '', value: '', date: '' }
            : { time: '', value: '', description: '' };
        
        // No API call here - just add to local form state
        // All changes will be saved when user clicks "Update Plan"
        
        setFormData(prev => ({
            ...prev,
            events: [...prev.events, newEvent]
        }));
    };

    const removeEvent = (index) => {
        // No API call here - just remove from local form state
        // All changes will be saved when user clicks "Update Plan"
        
        const newEvents = formData.events.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, events: newEvents }));
    };

    const updateEvent = (index, field, value) => {
        const newEvents = [...formData.events];
        newEvents[index] = { ...newEvents[index], [field]: value };
        setFormData(prev => ({ ...prev, events: newEvents }));
        
        // No API call here - just update the local form state
        // All changes will be saved when user clicks "Update Plan"
    };

    // Drag and Drop functions
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.target.style.opacity = '0.5';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    };

    const handleDragEnter = (e, index) => {
        e.preventDefault();
        setDragOverIndex(index);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOverIndex(null);
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const newEvents = [...formData.events];
        const draggedEvent = newEvents[draggedIndex];
        newEvents.splice(draggedIndex, 1);
        newEvents.splice(dropIndex, 0, draggedEvent);
        
        setFormData(prev => ({ ...prev, events: newEvents }));
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = '1';
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const validateForm = () => {
        const newErrors = {};
        
        console.log('=== VALIDATE FORM START ===');
        console.log('formData:', formData);
        
        if (!formData.name.trim()) {
            newErrors.name = 'Action plan name is required';
        }
        
        if (formData.carePlanSelection === 'new' && !formData.carePlanName.trim()) {
            newErrors.carePlanName = 'Care plan name is required';
        }
        
        if (!formData.startDate) {
            newErrors.startDate = 'Start date is required';
        } else {
            // Validate that start date is not in the past, with special handling for same-day plans
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Set to start of today
            
            // Parse the date string using local timezone to avoid UTC conversion issues
            const [year, month, day] = formData.startDate.split('-').map(Number);
            const startDateOnly = new Date(year, month - 1, day); // month is 0-indexed
            
            console.log('Start date validation:', {
                formDataStartDate: formData.startDate,
                year, month, day,
                startDateOnly: startDateOnly.toISOString(),
                today: today.toISOString(),
                isSameDay: isSameDayPlan(formData.startDate),
                willFail: startDateOnly < today && !isSameDayPlan(formData.startDate)
            });
            
            if (startDateOnly < today && !isSameDayPlan(formData.startDate)) {
                newErrors.startDate = 'Start date cannot be in the past (except for same-day plans)';
            }
        }
        
        if (formData.type === 'seasonal' && !formData.endDate) {
            newErrors.endDate = 'End date is required for seasonal plans';
        }
        
        if (formData.events.length === 0) {
            newErrors.events = 'At least one event is required';
        }
        
        // Validate care plan selection for new plans
        if (!plan && formData.carePlanSelection === 'existing' && !formData.selectedCarePlanId) {
            newErrors.selectedCarePlanId = 'Please select an existing care plan';
        }
        
        // Validate events
        formData.events.forEach((event, index) => {
            if (formData.type === 'seasonal') {
                if (!event.month || !event.value || !event.date) {
                    newErrors.events = 'All seasonal events must have month, value, and date';
                } else {
                    // Validate that event date is not in the past, with special handling for same-day events
                    const today = new Date();
                    today.setHours(0, 0, 0, 0); // Set to start of today
                    
                    // Parse the date string using local timezone to avoid UTC conversion issues
                    const [year, month, day] = event.date.split('-').map(Number);
                    const eventDateOnly = new Date(year, month - 1, day); // month is 0-indexed
                    
                    if (eventDateOnly < today && !isSameDayPlan(event.date)) {
                        newErrors.events = `Event ${index + 1} date cannot be in the past (except for same-day events)`;
                    }
                }
            } else {
                if (!event.time || !event.value) {
                    newErrors.events = 'All daily events must have time and value';
                }
            }
        });

        // Validate that event dates fall within the plan date range
        if (formData.type === 'seasonal' && !validateEventDates()) {
            newErrors.events = 'All event dates must fall within the plan start and end date range';
        }
        
        console.log('=== VALIDATE FORM END ===');
        console.log('newErrors:', newErrors);
        console.log('validation passed:', Object.keys(newErrors).length === 0);
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Helper function to get valid months within date range
    const getValidMonths = () => {
        if (!formData.startDate) {
            // If no start date, return all months
            return [
                { value: 'Jan', name: 'January', num: 0 },
                { value: 'Feb', name: 'February', num: 1 },
                { value: 'Mar', name: 'March', num: 2 },
                { value: 'Apr', name: 'April', num: 3 },
                { value: 'May', name: 'May', num: 4 },
                { value: 'Jun', name: 'June', num: 5 },
                { value: 'Jul', name: 'July', num: 6 },
                { value: 'Aug', name: 'August', num: 7 },
                { value: 'Sep', name: 'September', num: 8 },
                { value: 'Oct', name: 'October', num: 9 },
                { value: 'Nov', name: 'November', num: 10 },
                { value: 'Dec', name: 'December', num: 11 }
            ];
        }
        
        const startDate = new Date(formData.startDate);
        const endDate = formData.endDate ? new Date(formData.endDate) : new Date(startDate.getFullYear() + 1, 11, 31); // Next year if no end date
        
        const months = [
            { value: 'Jan', name: 'January', num: 0 },
            { value: 'Feb', name: 'February', num: 1 },
            { value: 'Mar', name: 'March', num: 2 },
            { value: 'Apr', name: 'April', num: 3 },
            { value: 'May', name: 'May', num: 4 },
            { value: 'Jun', name: 'June', num: 5 },
            { value: 'Jul', name: 'July', num: 6 },
            { value: 'Aug', name: 'August', num: 7 },
            { value: 'Sep', name: 'September', num: 8 },
            { value: 'Oct', name: 'October', num: 9 },
            { value: 'Nov', name: 'November', num: 10 },
            { value: 'Dec', name: 'December', num: 11 }
        ];
        
        return months.filter(month => {
            // For each month, check if it falls within the date range
            // considering that the automation could span multiple years
            
            const startMonth = startDate.getMonth();
            const startYear = startDate.getFullYear();
            const endMonth = endDate.getMonth();
            const endYear = endDate.getFullYear();
            
            // If it's the same year, simple month range check
            if (startYear === endYear) {
                return month.num >= startMonth && month.num <= endMonth;
            }
            
            // If spans multiple years, month is valid if:
            // 1. It's in the start year and >= start month, OR
            // 2. It's in the end year and <= end month, OR
            // 3. It's in any year between start and end years
            return (month.num >= startMonth) || (month.num <= endMonth);
        });
    };

    // Helper function to get min and max dates for event date inputs
    const getDateConstraints = () => {
        return {
            min: formData.startDate || undefined,
            max: formData.endDate || undefined
        };
    };

    // Enhanced validation to check if event dates fall within the plan date range
    const validateEventDates = () => {
        if (!formData.startDate) return true;
        
        const startDate = new Date(formData.startDate);
        const endDate = formData.endDate ? new Date(formData.endDate) : null;
        
        return formData.events.every(event => {
            if (!event.date) return true; // Skip validation if no date set yet
            
            const eventDate = new Date(event.date);
            
            // Check if event date is not before start date
            if (eventDate < startDate) return false;
            
            // Check if event date is not after end date (if end date exists)
            if (endDate && eventDate > endDate) return false;
            
            return true;
        });
    };

    // Helper function to check if plan is created on the same day (12AM and after)
    const isSameDayPlan = (startDate) => {
        if (!startDate) return false;
        
        // Parse the date string using local timezone to avoid UTC conversion issues
        const [year, month, day] = startDate.split('-').map(Number);
        const planDateOnly = new Date(year, month - 1, day); // month is 0-indexed
        
        const today = new Date();
        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        
        // Compare the date-only objects
        const isSameDay = planDateOnly.getTime() === todayOnly.getTime();
        
        console.log('isSameDayPlan check:', {
            startDate,
            year, month, day,
            planDateOnly: planDateOnly.toISOString(),
            todayOnly: todayOnly.toISOString(),
            isSameDay
        });
        
        return isSameDay;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('AutomationPlanModal handleSubmit called');
        
        if (!validateForm()) {
            console.log('Form validation failed');
            return;
        }

        console.log('Form validation passed, calling onSave with formData:', formData);
        setIsSubmitting(true);
        
        try {
            // Save everything including events - both for new and existing plans
            await onSave(formData);
        } catch (error) {
            console.error('Error saving automation plan:', error);
            setErrors({ submit: error.message || 'Failed to save automation plan' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setErrors({});
        setIsSubmitting(false);
        onClose();
    };

    const getActionTypeLabel = () => {
        const labels = {
            light: 'Light Schedule',
            irrigation: 'Watering Schedule',
            fan: 'Fan Schedule',
            humidity: 'Humidity Schedule'
        };
        return labels[actionType] || 'Automation Schedule';
    };

    const getValueLabelAndPlaceholder = (actuatorType) => {
        const unit = getActuatorUnit(actuatorType);
        
        const labels = {
            light: { label: `Value (hours)`, placeholder: '10' },
            irrigation: { label: `Value (inches)`, placeholder: '1' },
            humidity: { label: `Value (%)`, placeholder: '75' },
            temperature: { label: `Value (°F)`, placeholder: '72' },
            fan: { label: `Value (%)`, placeholder: '75' },
            camera: { label: `Value (on/off)`, placeholder: 'on' }
        };
        
        return labels[actuatorType] || { label: `Value (${unit})`, placeholder: '1' };
    };

    // Removed default events - users will create their own events


    if (!isVisible) return null;

    return (
        <div className="automation-modal-overlay" onClick={handleClose}>
            <div className="automation-modal" onClick={(e) => e.stopPropagation()}>
                <div className="automation-modal-header">
                    <h3>{plan ? 'Edit Automation Plan' : 'Create New Automation Plan'}</h3>
                    <button className="automation-modal-close" onClick={handleClose}>
                        <i className="ri-close-line"></i>
                    </button>
                </div>
                
                <form className="automation-form" onSubmit={handleSubmit}>
                    <div className="automation-form-content">
                        {errors.submit && (
                            <div className="automation-error-message">
                                {errors.submit}
                            </div>
                        )}
                        
                        {/* Action Plan Name */}
                        <div className="form-group">
                            <label htmlFor="name">Action Plan Name *</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className={errors.name ? 'error' : ''}
                                placeholder={`e.g., ${getActionTypeLabel()}`}
                                required
                            />
                            {errors.name && <span className="error-message">{errors.name}</span>}
                        </div>
                        
                        {/* Care Plan Selection */}
                        {!plan && (
                            <div className="form-group">
                                <label>Care Plan Selection *</label>
                                <div className="care-plan-selection">
                                    <div className="radio-group">
                                        <label className="radio-label">
                                            <input
                                                type="radio"
                                                name="carePlanSelection"
                                                value="new"
                                                checked={formData.carePlanSelection === 'new'}
                                                onChange={handleInputChange}
                                            />
                                            <span className="radio-text">Create New Care Plan</span>
                                        </label>
                                        <label className="radio-label">
                                            <input
                                                type="radio"
                                                name="carePlanSelection"
                                                value="existing"
                                                checked={formData.carePlanSelection === 'existing'}
                                                onChange={handleInputChange}
                                            />
                                            <span className="radio-text">Add to Existing Care Plan</span>
                                        </label>
                                    </div>
                                    
                                                            {/* Care Plan Name (only show for new care plans) */}
                        {formData.carePlanSelection === 'new' && (
                            <div className="new-care-plan-input">
                                <label htmlFor="carePlanName">Care Plan Name *</label>
                                <input
                                    type="text"
                                    id="carePlanName"
                                    name="carePlanName"
                                    value={formData.carePlanName}
                                    onChange={handleInputChange}
                                    className={errors.carePlanName ? 'error' : ''}
                                    placeholder="e.g., Winter Growth Cycle"
                                    required
                                />
                                {errors.carePlanName && <span className="error-message">{errors.carePlanName}</span>}
                                
                                <label htmlFor="description">Care Plan Description</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    className={errors.description ? 'error' : ''}
                                    placeholder="e.g., Automated care for winter plants"
                                    rows="3"
                                />
                                {errors.description && <span className="error-message">{errors.description}</span>}
                            </div>
                        )}
                                    
                                    {formData.carePlanSelection === 'existing' && (
                                        <div className="existing-care-plan-selector">
                                            <label htmlFor="selectedCarePlanId">Select Care Plan *</label>
                                            <select
                                                id="selectedCarePlanId"
                                                name="selectedCarePlanId"
                                                value={formData.selectedCarePlanId || ''}
                                                onChange={handleInputChange}
                                                required={formData.carePlanSelection === 'existing'}
                                            >
                                                <option value="">Choose a care plan...</option>
                                                {carePlans?.map(carePlan => (
                                                    <option key={carePlan.care_plan_id} value={carePlan.care_plan_id}>
                                                        {carePlan.name}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.selectedCarePlanId && (
                                                <span className="error-message">{errors.selectedCarePlanId}</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {/* Plan Type */}
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="type">Plan Type *</label>
                                <select
                                    id="type"
                                    name="type"
                                    value={formData.type}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="seasonal">🌱 Seasonal Schedule</option>
                                    <option value="daily">🕐 Daily Schedule</option>
                                </select>
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="recurrence">Recurrence *</label>
                                <select
                                    id="recurrence"
                                    name="recurrence"
                                    value={formData.recurrence}
                                    onChange={handleInputChange}
                                    required
                                >
                                    {formData.type === 'seasonal' ? (
                                        <>
                                            <option value="monthly">Monthly</option>
                                            <option value="weekly">Weekly</option>
                                        </>
                                    ) : (
                                        <>
                                            <option value="daily">Daily</option>
                                            <option value="weekly">Weekly</option>
                                        </>
                                    )}
                                </select>
                            </div>
                        </div>
                        
                        {/* Date Range */}
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="startDate">Start Date *</label>
                                <CalendarPicker
                                    value={formData.startDate}
                                    onChange={(date) => setFormData(prev => ({ ...prev, startDate: date }))}
                                    placeholder="Select start date"
                                    required
                                    error={errors.startDate}
                                />
                                {errors.startDate && <span className="error-message">{errors.startDate}</span>}
                                {formData.startDate && isSameDayPlan(formData.startDate) && (
                                    <span className="info-message">
                                        ✓ Same-day plan - will be active today
                                    </span>
                                )}
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="endDate">
                                    End Date {formData.type === 'seasonal' ? '*' : '(Optional)'}
                                </label>
                                <CalendarPicker
                                    value={formData.endDate}
                                    onChange={(date) => setFormData(prev => ({ ...prev, endDate: date }))}
                                    placeholder="Select end date"
                                    required={formData.type === 'seasonal'}
                                    error={errors.endDate}
                                    minDate={formData.startDate || undefined}
                                />
                                {errors.endDate && <span className="error-message">{errors.endDate}</span>}
                            </div>
                        </div>
                        
                        {/* Annual Repeat */}
                        {formData.type === 'seasonal' && (
                            <div className="form-group checkbox-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="repeatsAnnually"
                                        checked={formData.repeatsAnnually}
                                        onChange={handleInputChange}
                                    />
                                    <span className="checkmark"></span>
                                    <span className="checkbox-text">
                                        <i className="ri-refresh-line"></i>
                                        Repeat this schedule annually
                                    </span>
                                </label>
                            </div>
                        )}
                        
                        {/* Events Section */}
                        <div className="events-section">
                            <div className="events-header">
                                <h4>Schedule Events</h4>
                                <div className="events-actions">
                                    <button 
                                        type="button" 
                                        className="add-event-btn"
                                        onClick={addEvent}
                                    >
                                        <i className="ri-add-line"></i>
                                        Add Event
                                    </button>
                                </div>
                            </div>
                            <p className="events-hint">
                                <i className="ri-drag-move-line"></i>
                                Drag and drop events to reorder them
                            </p>
                            
                            {errors.events && (
                                <div className="error-message">{errors.events}</div>
                            )}
                            
                            <div className="events-list">
                                {formData.events.map((event, index) => (
                                    <div 
                                        key={index} 
                                        className={`event-item ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, index)}
                                        onDragOver={(e) => handleDragOver(e, index)}
                                        onDragEnter={(e) => handleDragEnter(e, index)}
                                        onDragLeave={handleDragLeave}
                                        onDrop={(e) => handleDrop(e, index)}
                                        onDragEnd={handleDragEnd}
                                    >
                                        {formData.type === 'seasonal' ? (
                                            <>
                                                <div className="event-field">
                                                    <label>Month</label>
                                                    <select
                                                        value={event.month || ''}
                                                        onChange={(e) => updateEvent(index, 'month', e.target.value)}
                                                    >
                                                        <option value="">Select month</option>
                                                        {getValidMonths().map(month => (
                                                            <option key={month.value} value={month.value}>
                                                                {month.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="event-field">
                                                    <label>{getValueLabelAndPlaceholder(actionType).label}</label>
                                                    <input
                                                        type="text"
                                                        value={event.value || ''}
                                                        onChange={(e) => updateEvent(index, 'value', e.target.value)}
                                                        placeholder={getValueLabelAndPlaceholder(actionType).placeholder}
                                                    />
                                                </div>
                                                <div className="event-field">
                                                    <label>Date</label>
                                                    <div className="date-input-wrapper">
                                                        <input
                                                            type="date"
                                                            value={event.date || ''}
                                                            onChange={(e) => updateEvent(index, 'date', e.target.value)}
                                                            className="date-input"
                                                            min={getDateConstraints().min}
                                                            max={getDateConstraints().max}
                                                        />
                                                        <i className="ri-calendar-line date-picker-icon"></i>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="event-field">
                                                    <label>Time</label>
                                                    <div className="time-input-wrapper">
                                                        <input
                                                            type="time"
                                                            value={event.time || ''}
                                                            onChange={(e) => updateEvent(index, 'time', e.target.value)}
                                                            className="time-input"
                                                        />
                                                        <i className="ri-time-line time-picker-icon"></i>
                                                    </div>
                                                </div>
                                                <div className="event-field">
                                                    <label>{getValueLabelAndPlaceholder(actionType).label}</label>
                                                    <input
                                                        type="text"
                                                        value={event.value || ''}
                                                        onChange={(e) => updateEvent(index, 'value', e.target.value)}
                                                        placeholder={getValueLabelAndPlaceholder(actionType).placeholder}
                                                    />
                                                </div>
                                                <div className="event-field">
                                                    <label>Description</label>
                                                    <input
                                                        type="text"
                                                        value={event.description || ''}
                                                        onChange={(e) => updateEvent(index, 'description', e.target.value)}
                                                        placeholder="e.g., Morning humidity"
                                                    />
                                                </div>
                                            </>
                                        )}
                                        <button 
                                            type="button"
                                            className="remove-event-btn"
                                            onClick={() => removeEvent(index)}
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    
                    <div className="automation-modal-actions">
                        <button 
                            type="button" 
                            className="automation-modal-cancel" 
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="automation-modal-save"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Saving...' : (plan ? 'Update Plan' : 'Create Plan')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

AutomationPlanModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    plan: PropTypes.object,
    actionType: PropTypes.string.isRequired
};

export default AutomationPlanModal; 